import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from 'react'

const FIELD_CONTROL =
  'w-full rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] ' +
  'bg-[var(--xr-bg-surface)] px-3 py-2 text-sm text-[var(--xr-text-primary)] ' +
  'placeholder:text-[var(--xr-text-muted)] ' +
  'focus:outline-none focus:border-[var(--xr-brand-primary)] focus:ring-2 ' +
  'focus:ring-[var(--xr-brand-primary-soft)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

export interface FieldProps {
  label: string
  hint?: string
  error?: string
  className?: string
  /** Compact label style used by the route editor's right rail. The label
   *  renders as uppercase muted text (matching `RouteDetailPage` read-view
   *  section headings) so the whole intro card looks like one continuous
   *  document instead of a stack of bordered cards. */
  compact?: boolean
  children: ReactNode
}

export function Field({ label, hint, error, className, compact = false, children }: FieldProps) {
  const rootClass = ['block', className].filter(Boolean).join(' ')
  const labelClass = compact
    ? 'block mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]'
    : 'block text-sm font-medium text-[var(--xr-text-primary)] mb-1'
  return (
    <label className={rootClass}>
      <span className={labelClass}>{label}</span>
      {children}
      {hint && !error && (
        <span className="mt-1 block text-xs text-[var(--xr-text-muted)]">{hint}</span>
      )}
      {error && <span className="mt-1 block text-xs text-[var(--xr-danger)]">{error}</span>}
    </label>
  )
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...rest }, ref) {
    return <input ref={ref} className={`${FIELD_CONTROL} ${className}`.trim()} {...rest} />
  },
)

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`${FIELD_CONTROL} ${className}`.trim()} {...rest} />
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = '', children, ...rest }, ref) {
    return (
      <select ref={ref} className={`${FIELD_CONTROL} ${className}`.trim()} {...rest}>
        {children}
      </select>
    )
  },
)
